import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Stack,
  Paper,
  Group,
  Box,
  Badge,
  Button,
  Divider,
  Alert,
  Skeleton,
  Tabs,
  ActionIcon,
  Tooltip,
  SegmentedControl
} from '@mantine/core';
import {
  IconMapPin,
  IconCircleCheck,
  IconCircleX,
  IconEdit,
  IconArrowLeft,
  IconAlertTriangle,
  IconWorldWww,
  IconRefresh,
  IconClock
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { RegionRepository } from '../../../infrastructure/repositories/RegionRepository';
import { apiClient } from '../../../infrastructure/api/ApiClient';
import { RegionDetail, RegionCrawlerStats } from '../../../domain/entities/Region';
import { SubAreasManager } from '../../components/domain/SubAreasManager';
import { EditRegionModal } from '../../components/domain/EditRegionModal';
import { ManageSubredditsModal } from '../../components/domain/ManageSubredditsModal';
import { Breadcrumbs } from '../../components/common/Breadcrumbs';
import { ADMIN_ROUTES } from '../../routing/routes';
import { CrawlerSummaryCards, SubredditStatsTable, QueueHealthChart } from '../../components/admin/RegionStats';

/**
 * RegionDetailPage - Admin-only page for viewing and managing a single region
 *
 * Features:
 * - View region details
 * - Manage SubAreas (via SubAreasManager component)
 * - View subreddits
 * - Edit region (future)
 * - View statistics
 *
 * Security:
 * - Admin-only access enforced by route protection
 * - Backend enforces all authorization
 */
export const RegionDetailPage: React.FC = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [region, setRegion] = useState<RegionDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isManageSubredditsOpen, setIsManageSubredditsOpen] = useState<boolean>(false);

  // SESSION_400: Crawler statistics state
  const [crawlerStats, setCrawlerStats] = useState<RegionCrawlerStats | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsDays, setStatsDays] = useState<string>('1');

  const regionRepository = new RegionRepository(apiClient);

  /**
   * Fetch region details
   */
  const fetchRegion = useCallback(async (): Promise<void> => {
    if (!id) {
      setError('No region ID provided');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedRegion = await regionRepository.getById(id);
      setRegion(fetchedRegion);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load region';
      setError(errorMessage);
      setRegion(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  /**
   * Fetch region on mount
   */
  useEffect(() => {
    void fetchRegion();
  }, [fetchRegion]);

  /**
   * SESSION_400: Fetch crawler statistics for the region
   */
  const fetchCrawlerStats = useCallback(async (): Promise<void> => {
    if (!id) return;

    setIsStatsLoading(true);
    setStatsError(null);

    try {
      const stats = await regionRepository.getCrawlerStats(id, parseInt(statsDays, 10));
      setCrawlerStats(stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load crawler statistics';
      setStatsError(errorMessage);
      setCrawlerStats(null);
    } finally {
      setIsStatsLoading(false);
    }
  }, [id, statsDays]);

  /**
   * SESSION_400: Fetch stats when days selection changes
   */
  useEffect(() => {
    if (id) {
      void fetchCrawlerStats();
    }
  }, [id, statsDays, fetchCrawlerStats]);

  /**
   * Handle SubArea changes (refetch region to update SubArea count)
   */
  const handleSubAreaChange = useCallback((): void => {
    void fetchRegion();
  }, [fetchRegion]);

  /**
   * Navigation handlers
   */
  const handleBack = useCallback((): void => {
    navigate(ADMIN_ROUTES.REGIONS);
  }, [navigate]);

  /**
   * Render loading skeleton
   */
  if (isLoading) {
    return (
      <Stack gap="lg">
        <Group>
          <Skeleton height={36} width={100} />
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Stack gap="md">
            <Skeleton height={32} width={300} />
            <Skeleton height={20} width={400} />
            <Divider />
            <Skeleton height={100} />
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Skeleton height={400} />
        </Paper>
      </Stack>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Stack gap="lg">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
          Back to Regions
        </Button>

        <Alert icon={<IconAlertTriangle />} title="Error Loading Region" color="red" variant="light">
          {error}
        </Alert>

        <Text c="dimmed" ta="center">
          The region could not be loaded. It may have been deleted or you may not have permission to view it.
        </Text>
      </Stack>
    );
  }

  /**
   * Render region not found
   */
  if (!region) {
    return (
      <Stack gap="lg">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
          Back to Regions
        </Button>

        <Alert icon={<IconAlertTriangle />} title="Region Not Found" color="yellow" variant="light">
          The requested region could not be found.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'Regions', href: ADMIN_ROUTES.REGIONS },
          { label: region.name }
        ]}
      />

      {/* Region Overview */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="md">
            <IconMapPin size={32} color="var(--mantine-color-blue-6)" />
            <Box>
              <Title order={2} ff="Comfortaa, sans-serif">
                {region.name}
              </Title>
              <Text size="sm" c="dimmed" mt={4}>
                {region.regionId}
              </Text>
            </Box>
          </Group>

          <Group gap="sm">
            {region.isActive ? (
              <Badge size="lg" color="green" variant="light" leftSection={<IconCircleCheck size={16} />}>
                Active
              </Badge>
            ) : (
              <Badge size="lg" color="gray" variant="light" leftSection={<IconCircleX size={16} />}>
                Inactive
              </Badge>
            )}

            <Tooltip label="Edit Region">
              <ActionIcon variant="light" color="blue" size="lg" onClick={() => setIsEditModalOpen(true)}>
                <IconEdit size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <Divider mb="md" />

        {/* Region Details */}
        <Stack gap="sm">
          {/* Location */}
          <Group gap="xs">
            <Text fw={600} size="sm" c="dimmed" miw={100}>
              Location:
            </Text>
            <Text size="sm">
              {[region.city, region.state, region.country].filter(Boolean).join(', ')}
            </Text>
          </Group>

          {/* SubAreas */}
          <Group gap="xs">
            <Text fw={600} size="sm" c="dimmed" miw={100}>
              SubAreas:
            </Text>
            <Badge variant="light" color={region.subAreaCount === 0 ? 'red' : 'blue'}>
              {region.subAreaCount} SubArea{region.subAreaCount !== 1 ? 's' : ''}
            </Badge>
          </Group>

          {/* Subreddits */}
          <Group gap="xs" align="flex-start">
            <Text fw={600} size="sm" c="dimmed" miw={100}>
              Subreddits:
            </Text>
            <Box style={{ flex: 1, display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {region.subreddits.length > 0 ? (
                region.subreddits.map((subreddit) => (
                  <Badge key={subreddit} variant="outline" leftSection={<IconWorldWww size={14} />}>
                    r/{subreddit}
                  </Badge>
                ))
              ) : (
                <Text size="sm" c="dimmed">
                  No subreddits configured
                </Text>
              )}
              <Button
                size="xs"
                variant="filled"
                onClick={() => setIsManageSubredditsOpen(true)}
              >
                Manage
              </Button>
            </Box>
          </Group>

          {/* Description */}
          {region.description && (
            <Group gap="xs" align="flex-start">
              <Text fw={600} size="sm" c="dimmed" miw={100}>
                Description:
              </Text>
              <Text size="sm">{region.description}</Text>
            </Group>
          )}

          {/* Created At */}
          <Group gap="xs">
            <Text fw={600} size="sm" c="dimmed" miw={100}>
              Created:
            </Text>
            <Text size="sm">{new Date(region.createdAt).toLocaleDateString()}</Text>
          </Group>

          {/* Updated At */}
          {region.updatedAt && (
            <Group gap="xs">
              <Text fw={600} size="sm" c="dimmed" miw={100}>
                Last Updated:
              </Text>
              <Text size="sm">{new Date(region.updatedAt).toLocaleDateString()}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      {/* Tabs for Different Sections */}
      <Tabs defaultValue="subareas">
        <Tabs.List>
          <Tabs.Tab value="subareas">SubAreas</Tabs.Tab>
          <Tabs.Tab value="statistics">Statistics</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="subareas" pt="md">
          {/* SubAreasManager Component */}
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <SubAreasManager
              regionId={region.regionId}
              userRole={user?.role ?? 'BusinessOwner'}
              readOnly={false}
              onSubAreaChange={handleSubAreaChange}
            />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="statistics" pt="md">
          <Stack gap="md">
            {/* Time Range Selector and Refresh */}
            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="center">
                <Group gap="md">
                  <Group gap="xs">
                    <IconClock size={18} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" fw={500}>Time Range:</Text>
                  </Group>
                  <SegmentedControl
                    size="sm"
                    value={statsDays}
                    onChange={setStatsDays}
                    data={[
                      { label: '24 hours', value: '1' },
                      { label: '7 days', value: '7' },
                      { label: '30 days', value: '30' }
                    ]}
                  />
                </Group>
                <Group gap="sm">
                  {crawlerStats?.collectedAt && (
                    <Text size="xs" c="dimmed">
                      Updated: {new Date(crawlerStats.collectedAt).toLocaleTimeString()}
                    </Text>
                  )}
                  <Tooltip label="Refresh statistics">
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={(): void => void fetchCrawlerStats()}
                      loading={isStatsLoading}
                    >
                      <IconRefresh size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Paper>

            {/* Error State */}
            {statsError && (
              <Alert icon={<IconAlertTriangle />} title="Error Loading Statistics" color="red" variant="light">
                {statsError}
              </Alert>
            )}

            {/* Summary Cards */}
            <CrawlerSummaryCards stats={crawlerStats} isLoading={isStatsLoading} />

            {/* S_031_C_004: Queue Health History Chart */}
            <QueueHealthChart regionId={id ?? ''} />

            {/* Subreddit Stats Table */}
            <SubredditStatsTable
              stats={crawlerStats?.subredditStats ?? []}
              isLoading={isStatsLoading}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Warning if no SubAreas */}
      {region.subAreaCount === 0 && (
        <Alert icon={<IconAlertTriangle />} title="No SubAreas Configured" color="yellow" variant="light">
          This region has no SubAreas. Add at least one SubArea to enable business imports and mention tracking.
        </Alert>
      )}

      {/* Edit Region Modal */}
      <EditRegionModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        region={region}
        onSuccess={() => void fetchRegion()}
      />

      {/* Manage Subreddits Modal */}
      <ManageSubredditsModal
        isOpen={isManageSubredditsOpen}
        onClose={() => setIsManageSubredditsOpen(false)}
        regionId={region.id}
        currentSubreddits={region.subreddits}
        onSuccess={() => void fetchRegion()}
      />
    </Stack>
  );
};
